import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { CustomDivider } from "@/components/CustomDivider";
import { ArrowIcon, Button as Button95, Checkbox, Hourglass, NumberInput, Panel, Slider, Text, TextInput } from 'react95-native-rabbl';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller, Resolver } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { AppText } from '@/components/AppText';
import { MatchesFilter } from '@/components/MatchesFilter';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
});

export default function Filter() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const dispatch = useDispatch();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);

  const onSubmit = useCallback(async(ageMin : number, ageMax : number, heightMin : number, heightMax : number, maxDistance : number) => {
    try {
      showSpinner();

      if (ageMin < AppConsts.MIN_AGE || ageMax > AppConsts.MAX_AGE || ageMin > ageMax) {
        throw new Error('Invalid ages');
      }

      const response = await AppUtilities.fetchAuthorized(
        "Filter.onSubmit",
        "/rabbl/api/set_filter",
        "POST",
        {
          domain: domain,
          age_min: ageMin,
          age_max: ageMax,
          height_min: heightMin,
          height_max: heightMax,
          desired_max_distance: maxDistance,
        }
      );
      const membership: CommunityMembership = ((response as any).body as any).community_membership;
      dispatch(setData(response));
      if (router.canGoBack()) {
        router.back();
      } else {
        router.navigate(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(ageMin, ageMax, heightMin, heightMax, maxDistance), true);
      AppUtilities.handleException(e, "Filter.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [domain]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Filter"} />

      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <MatchesFilter
            onSubmit={onSubmit}
            baselineAgeMin={communityMembership.user_info.desired_age_min ?? AppConsts.MIN_AGE}
            baselineAgeMax={communityMembership.user_info.desired_age_max ?? AppConsts.MAX_AGE}
            baselineHeightMin={(communityMembership.user_info.desired_height_min ?? -1) == -1 ? AppConsts.MIN_HEIGHT_INCHES : (communityMembership.user_info.desired_height_min ?? AppConsts.MIN_HEIGHT_INCHES)}
            baselineHeightMax={(communityMembership.user_info.desired_height_max ?? -1) == -1 ? AppConsts.MAX_HEIGHT_INCHES : (communityMembership.user_info.desired_height_max ?? AppConsts.MAX_HEIGHT_INCHES)}
            baselineMaxDistance={communityMembership.user_info.desired_max_distance_miles ?? 0}
          />
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
